import { Currency } from '~/components';
import { useNumberFormat } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';
import AverageDealSizeIcon from '../assets/average-deal.svg?react';
import GrossPipelineIcon from '../assets/conversion-rate.svg?react';
import OpportunitiesIcon from '../assets/opportunities.svg?react';
import WeightedPipelineIcon from '../assets/weighted-pipeline.svg?react';
import { useApi, useWorkspace } from '~/contexts';
import { useHistory } from 'react-router-dom';
import { QueryString } from '~/utils';

const Summary = styled.div`
  display: flex;
  align-items: center;
  background: ${colors.white};
  box-shadow: 0 3px 15px ${colors.grey10};
  border-radius: 5px;
  transition: opacity 250ms;
  opacity: ${({ fade }) => (fade ? 0.2 : 1)};
  min-width: fit-content;
  height: 6rem;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  cursor: pointer;
`;

const SummaryBox = styled.div`
  display: flex;
  flex: 1;
  padding: 1.5rem 1rem;
  justify-content: center;
  align-items: center;

  &:not(:first-child) {
    border-left: 1px solid ${colors.grey10};
  }
`;

const SummaryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  width: 3.125rem;
  height: 3.125rem;
`;

const SummaryAmount = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding-left: 1rem;
  font-weight: ${weights.bold};
  font-size: 1rem;

  p {
    font-size: 0.75rem;
    font-weight: ${weights.normal};
    line-height: 1rem;
    white-space: nowrap;
  }
`;

function OpportunitiesSummaryWidget({
  q,
  opportunityStageCategoryId,
  opportunityStageId,
  opportunityTagId,
  opportunityTypeId,
  opportunityAmountGreaterThanOrEqual,
  opportunitySalesforceSynced,
  opportunitySalesforceRecordType,
  opportunityHubspotSynced,
  opportunityOwnerId,
  opportunityPracticeId,
  opportunityBusinessUnitId,
  companyId,
  opportunityClosedStart,
  opportunityClosedEnd,
  opportunityOpenedStart,
  opportunityOpenedEnd,
  opportunityPipelineId,
}) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const [{ data, isReady }, setQuery] = useState({ data: null, isReady: false });

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).opportunities().getSummary({
      q,
      opportunityStageCategoryId,
      opportunityStageId,
      opportunityTagId,
      opportunityTypeId,
      opportunityAmountGreaterThanOrEqual,
      opportunitySalesforceSynced,
      opportunitySalesforceRecordType,
      opportunityHubspotSynced,
      opportunityOwnerId,
      opportunityPracticeId,
      opportunityBusinessUnitId,
      companyId,
      opportunityClosedStart,
      opportunityClosedEnd,
      opportunityOpenedStart,
      opportunityOpenedEnd,
      opportunityPipelineId,
    });

    setQuery({ data, isReady: true });
  }, [
    q,
    opportunityStageCategoryId,
    opportunityStageId,
    workspace.id,
    api,
    opportunityTagId,
    opportunityTypeId,
    opportunityAmountGreaterThanOrEqual,
    opportunitySalesforceSynced,
    opportunitySalesforceRecordType,
    opportunityHubspotSynced,
    opportunityOwnerId,
    opportunityPracticeId,
    opportunityBusinessUnitId,
    companyId,
    opportunityClosedStart,
    opportunityClosedEnd,
    opportunityOpenedStart,
    opportunityOpenedEnd,
    opportunityPipelineId,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const opportunities = useCallback(
    () =>
      `/app/${workspace.key}/reports/library/opportunities?${new QueryString(
        {
          opportunityStageCategoryId: opportunityStageCategoryId?.length ? opportunityStageCategoryId : 'all',
          opportunityStageId,
          opportunityTagId,
          opportunityTypeId,
          opportunityAmountGreaterThanOrEqual,
          opportunitySalesforceSynced,
          opportunitySalesforceRecordType,
          opportunityHubspotSynced,
          opportunityOwnerId,
          opportunityPracticeId,
          opportunityBusinessUnitId,
          companyId,
          opportunityClosedStart,
          opportunityClosedEnd,
          opportunityOpenedStart,
          opportunityOpenedEnd,
        },
        { multi: true },
      ).toString()}`,
    [
      workspace.key,
      opportunityStageCategoryId,
      opportunityStageId,
      opportunityTagId,
      opportunityTypeId,
      opportunityAmountGreaterThanOrEqual,
      opportunitySalesforceSynced,
      opportunitySalesforceRecordType,
      opportunityHubspotSynced,
      opportunityOwnerId,
      opportunityPracticeId,
      opportunityBusinessUnitId,
      companyId,
      opportunityClosedStart,
      opportunityClosedEnd,
      opportunityOpenedStart,
      opportunityOpenedEnd,
    ],
  );

  const history = useHistory();

  return (
    <Summary
      onClick={() => {
        history.push(opportunities());
      }}
      fade={!isReady}>
      {data && (
        <>
          <SummaryBox>
            <SummaryIcon>
              <OpportunitiesIcon />
            </SummaryIcon>
            <SummaryAmount>
              <div>
                {numberFormat.format(data.count)}
                <p>Opportunities</p>
              </div>
            </SummaryAmount>
          </SummaryBox>
          <SummaryBox>
            <SummaryIcon>
              <GrossPipelineIcon />
            </SummaryIcon>
            <SummaryAmount>
              <div>
                <Currency value={data.totalAmount} minimumFractionDigits={0} maximumFractionDigits={0} />
                <p>Gross Pipeline</p>
              </div>
            </SummaryAmount>
          </SummaryBox>
          <SummaryBox>
            <SummaryIcon>
              <WeightedPipelineIcon />
            </SummaryIcon>
            <SummaryAmount>
              <div>
                <Currency value={data.totalWeightedAmount} minimumFractionDigits={0} maximumFractionDigits={0} />
                <p>Weighted Pipeline</p>
              </div>
            </SummaryAmount>
          </SummaryBox>
          <SummaryBox>
            <SummaryIcon>
              <AverageDealSizeIcon />
            </SummaryIcon>
            <SummaryAmount>
              <div>
                <Currency value={data.averageDealSize} minimumFractionDigits={0} maximumFractionDigits={0} />
                <p>Average Opportunity</p>
              </div>
            </SummaryAmount>
          </SummaryBox>
        </>
      )}
    </Summary>
  );
}

export default OpportunitiesSummaryWidget;
